import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { templateApi, TemplateStructure, TemplateTopic, TemplateQuestion } from '../services/data_apis';
import { ArrowLeft, Save, Loader2, Edit2, Plus, Trash2, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface CreateTemplatePageProps {
  apiKey?: string;
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const CreateTemplatePage: React.FC<CreateTemplatePageProps> = ({
  apiKey,
  isDarkMode,
  onToggleTheme
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { query, apiKey: passedApiKey, researchLength } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templateData, setTemplateData] = useState<TemplateStructure | null>(null);
  
  // Multi-step flow states
  const [currentStep, setCurrentStep] = useState<'initial' | 'clarifying' | 'generating' | 'editing'>('initial');
  const [clarifyingQuestions, setClarifyingQuestions] = useState<Record<string, string>>({});
  const [clarifyingAnswers, setClarifyingAnswers] = useState<Record<string, string>>({});
  const [generationProgress, setGenerationProgress] = useState<string>('Initializing...');
  
  // Template save form
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  
  // Editing states
  const [editingTopic, setEditingTopic] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<any>({});

  useEffect(() => {
    if (query) {
      // Go straight to generation with progress tracking
      generateTemplate();
    } else {
      navigate('/home');
    }
  }, [query]);

  const generateTemplate = async () => {
    try {
      setLoading(true);
      setError(null);
      setCurrentStep('generating');
      setGenerationProgress('Starting template generation...');
      
      // Get Auth0 token for authentication
      let authToken: string | undefined;
      if (isAuthenticated) {
        try {
          authToken = await getAccessTokenSilently();
        } catch (authErr) {
          console.error('Failed to get auth token:', authErr);
          setError('Authentication failed. Please log in again.');
          return;
        }
      }
      
      // Update progress
      setGenerationProgress('Generating topics based on your answers...');
      
      const requestData: any = {
        query,
        research_length: researchLength || 'targeted'
      };
      
      if (passedApiKey || apiKey) {
        requestData.api_key = passedApiKey || apiKey;
      }
      
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev.includes('topics')) return 'Creating questions for each topic...';
          if (prev.includes('questions')) return 'Identifying relevant sources...';
          if (prev.includes('sources')) return 'Finalizing template structure...';
          return prev;
        });
      }, 3000);
      
      const response = await templateApi.createTemplate(requestData, authToken);
      
      clearInterval(progressInterval);
      
      if (response.success) {
        setTemplateData(response.data);
        setTemplateName(`Research Template - ${response.data.query.slice(0, 50)}${response.data.query.length > 50 ? '...' : ''}`);
        setCurrentStep('editing');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate template';
      
      // Provide more specific error message for API key issues
      if (errorMessage.includes('API key') || errorMessage.includes('unauthorized')) {
        setError('OpenAI API key is required to generate template structure (topics, questions, and sources). The key is only used for generation - saving the template to database does not require an API key.');
      } else if (errorMessage.includes('Not authenticated')) {
        setError('Authentication required. Please log in to create templates.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateData || !templateName.trim()) return;

    try {
      setSaving(true);
      setError(null);

      // Get Auth0 token for authentication
      let authToken: string | undefined;
      if (isAuthenticated) {
        try {
          authToken = await getAccessTokenSilently();
        } catch (authErr) {
          console.error('Failed to get auth token:', authErr);
          setError('Authentication failed. Please log in again.');
          return;
        }
      }

      const response = await templateApi.saveTemplate({
        name: templateName.trim(),
        description: templateDescription.trim(),
        query: templateData.query,
        research_length: templateData.research_length,
        topics: templateData.topics,
        is_public: isPublic
      }, authToken);

      if (response.success) {
        navigate('/templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const startTopicEdit = (topicId: string, currentTitle: string) => {
    setEditingTopic(topicId);
    setEditingValues({ title: currentTitle });
  };

  const cancelTopicEdit = () => {
    setEditingTopic(null);
    setEditingValues({});
  };

  const saveTopicEdit = () => {
    if (!templateData || !editingTopic || !editingValues.title?.trim()) return;

    setTemplateData({
      ...templateData,
      topics: templateData.topics.map(topic =>
        topic.id === editingTopic
          ? { ...topic, title: editingValues.title.trim() }
          : topic
      )
    });

    setEditingTopic(null);
    setEditingValues({});
  };

  const startQuestionEdit = (questionId: string, currentText: string) => {
    setEditingQuestion(questionId);
    setEditingValues({ text: currentText });
  };

  const cancelQuestionEdit = () => {
    setEditingQuestion(null);
    setEditingValues({});
  };

  const saveQuestionEdit = (topicId: string) => {
    if (!templateData || !editingQuestion || !editingValues.text?.trim()) return;

    setTemplateData({
      ...templateData,
      topics: templateData.topics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              questions: topic.questions.map(q =>
                q.id === editingQuestion
                  ? { ...q, text: editingValues.text.trim() }
                  : q
              )
            }
          : topic
      )
    });

    setEditingQuestion(null);
    setEditingValues({});
  };

  const addQuestion = (topicId: string) => {
    if (!templateData) return;

    const newQuestion: TemplateQuestion = {
      id: `new_q_${Date.now()}`,
      text: 'New question',
      sources: []
    };

    setTemplateData({
      ...templateData,
      topics: templateData.topics.map(topic =>
        topic.id === topicId
          ? { ...topic, questions: [...topic.questions, newQuestion] }
          : topic
      )
    });
  };

  const removeQuestion = (topicId: string, questionId: string) => {
    if (!templateData) return;

    setTemplateData({
      ...templateData,
      topics: templateData.topics.map(topic =>
        topic.id === topicId
          ? {
              ...topic,
              questions: topic.questions.filter(q => q.id !== questionId)
            }
          : topic
      )
    });
  };

  // Show generation progress
  if (loading || currentStep === 'generating') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Generating Template
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {generationProgress}
          </p>
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            This may take a few minutes...
          </div>
        </div>
      </div>
    );
  }

  if (error && !templateData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Template Generation Failed
            </h3>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <div className="flex gap-2">
              <Button onClick={generateTemplate} variant="outline" size="sm">
                Try Again
              </Button>
              <Button onClick={() => navigate('/home')} variant="ghost" size="sm">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No template data available</p>
          <Button onClick={() => navigate('/home')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/templates')}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Templates
            </Button>
            
            <div className="flex items-center gap-2">
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name..."
                className="w-64"
              />
              <Button
                onClick={handleSaveTemplate}
                disabled={saving || !templateName.trim()}
                className="bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Query Header and Table */}
          <Card>
            <CardHeader>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {templateData.query}
                </h2>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <Badge variant="outline">
                    Research Length: {templateData.research_length}
                  </Badge>
                  <span>Topics: {templateData.topics.length}</span>
                  <span>Questions: {templateData.topics.reduce((acc, topic) => acc + topic.questions.length, 0)}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <th className="text-left p-4 font-semibold text-gray-900 dark:text-gray-100 w-2/3">Question</th>
                      <th className="text-left p-4 font-semibold text-gray-900 dark:text-gray-100 w-1/3">Sources</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateData.topics.map((topic, topicIndex) => (
                      <React.Fragment key={topic.id}>
                        {/* Topic Header Row */}
                        <tr className="bg-blue-50 dark:bg-blue-950/30 border-b border-gray-200 dark:border-gray-700">
                          <td className="p-4 font-semibold text-blue-900 dark:text-blue-100" colSpan={2}>
                            {editingTopic === topic.id ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={editingValues.title || ''}
                                  onChange={(e) => setEditingValues({ ...editingValues, title: e.target.value })}
                                  className="flex-1 text-sm"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveTopicEdit();
                                    if (e.key === 'Escape') cancelTopicEdit();
                                  }}
                                  autoFocus
                                />
                                <Button size="sm" variant="ghost" onClick={saveTopicEdit}>
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={cancelTopicEdit}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <span>Topic {topicIndex + 1}: {topic.title}</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startTopicEdit(topic.id, topic.title)}
                                >
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                        
                        {/* Questions for this topic */}
                        {topic.questions.map((question, questionIndex) => (
                          <tr 
                            key={`${topic.id}-${question.id}`}
                            className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                          >
                            {/* Question Column */}
                            <td className="p-4 text-gray-900 dark:text-gray-100">
                              {editingQuestion === question.id ? (
                                <div className="flex items-center gap-2">
                                  <Textarea
                                    value={editingValues.text || ''}
                                    onChange={(e) => setEditingValues({ ...editingValues, text: e.target.value })}
                                    className="flex-1 min-h-[60px] text-sm"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && e.ctrlKey) saveQuestionEdit(topic.id);
                                      if (e.key === 'Escape') cancelQuestionEdit();
                                    }}
                                    autoFocus
                                  />
                                  <div className="flex flex-col gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => saveQuestionEdit(topic.id)}>
                                      <Check className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={cancelQuestionEdit}>
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-start justify-between gap-2">
                                  <span className="flex-1">{questionIndex + 1}. {question.text}</span>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => startQuestionEdit(question.id, question.text)}
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeQuestion(topic.id, question.id)}
                                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </td>
                            
                            {/* Sources Column */}
                            <td className="p-4 text-sm text-gray-600 dark:text-gray-400">
                              {question.sources && Array.isArray(question.sources) && question.sources.length > 0 
                                ? question.sources.join(', ')
                                : 'Source not specified'
                              }
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Add Topic Button */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Add new topic logic here
                  }}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New Topic
                </Button>
              </div>
            </CardContent>
          </Card>

      </div>
    </div>
  );
};

export default CreateTemplatePage;